import { Auth0Client, Auth0ClientOptions } from '@auth0/auth0-spa-js';
import get from 'lodash/get';

import { ERROR_STATUS } from '@futrli/errors';

import { Authenticator } from '../authenticator';
import { Auth0Credentials, Auth0User, CurrentUserCheck, UserAttribute } from './types';
import { transformAuth0User, TransformedAuth0User } from './utils';
import { APIClients, ApiClientType } from '../../api-client';
import { Logger } from '../../logger';
import { checkCurrentUser } from '../../users';
import { ROUTES, QUERY_PARAMS } from '../../app-core';

export class Auth0Authenticator extends Authenticator<Auth0ClientOptions> {
  private auth0Client: Auth0Client;
  // Auth0Authenticator needs to have CurrentUserCheck as this.auth0Client.getUser won't get correct id
  // of the user. It will get sageId Id and we need to have uuid for that user which is returned
  // by /users/current route -> CurrentUserCheck interface
  private currentUser: CurrentUserCheck | null;
  private isFetchingCurrentUser: boolean;
  private errorCurrentUser: boolean;

  constructor(config: Auth0ClientOptions, client: Auth0Client) {
    super(config);
    this.auth0Client = client;
    this.currentUser = null;
    this.isFetchingCurrentUser = false;
    this.errorCurrentUser = false;
  }

  public async fetchCredentials(): Promise<Auth0Credentials | never> {
    const idToken = await this.auth0Client.getIdTokenClaims();
    return { jwtToken: idToken?.__raw || '' };
  }

  public async hasExpiredCredentials(): Promise<boolean | never> {
    const isAuthenticated = await this.auth0Client.isAuthenticated();
    return !isAuthenticated;
  }

  public async signOut(showError?: boolean): Promise<void> {
    await this.auth0Client.logout({
      logoutParams: {
        returnTo: `${window.location.origin}${ROUTES.LOGIN}${showError ? `?${QUERY_PARAMS.SAGE_50_ERROR}=true` : ''}`
      }
    });
  }

  public async getCurrentAuthenticatedUser(currentUser?: CurrentUserCheck): Promise<TransformedAuth0User | null> {
    const user = await this.auth0Client.getUser<Auth0User>();
    // Save initial current user if there is any
    if (currentUser) {
      this.currentUser = currentUser;
    }
    if (user) {
      // If there isn't any current user and if current user is not being fetched
      // and if there isn't failing users/current request  -> fetch current user
      if (!this.currentUser && !this.isFetchingCurrentUser && !this.errorCurrentUser) {
        try {
          this.isFetchingCurrentUser = true;
          APIClients.setApiClient(ApiClientType.Auth0);
          const fetchedCurrentUser = await checkCurrentUser();
          this.currentUser = fetchedCurrentUser;
        } catch (e) {
          const errorStatus = get(e, 'response.status');
          // User has terminated session
          if (errorStatus === ERROR_STATUS._403) {
            Logger.error(
              "Can't set current user as currentUser route is returning 403 error -> user's session has been terminated"
            );
            this.signOut();
            return null;
          }
          this.errorCurrentUser = true;
          Logger.error(
            "Can't set current user as currentUser route is returning 404 error -> Newly signed up user didn't submit provision request yet"
          );
        } finally {
          this.isFetchingCurrentUser = false;
        }
      }
      return transformAuth0User(user, this.currentUser);
    }
    return null;
  }

  public updateCurrentUser(newCurrentUser?: CurrentUserCheck): void {
    if (newCurrentUser) {
      this.currentUser = newCurrentUser;
    }
  }

  public async getUserAttributes(user: Auth0User): Promise<UserAttribute[]> {
    const { attributes } = user;
    const userAttributes: UserAttribute[] = [];
    Object.keys(attributes).forEach(k => {
      userAttributes.push({ Name: k, Value: attributes[k] });
    });
    return userAttributes;
  }

  public async updateUserAttributes(_user: Auth0User, _attributes: Partial<Auth0User>): Promise<Auth0User> {
    throw new Error('updateUserAttributes is not supported for Auth0 via client-side.');
  }

  public async changePassword(_user: Auth0User, _oldPassword: string, _newPassword: string): Promise<void> {
    throw new Error('changePassword is not supported for Auth0 via client-side.');
  }

  public async completeNewPassword(
    _user: Auth0User,
    _password: string,
    _requiredAttributes: unknown = {}
  ): Promise<void> {
    throw new Error('completeNewPassword is not supported for Auth0.');
  }

  public async forgotPassword(_username: string): Promise<void> {
    throw new Error('forgotPassword is not supported for Auth0 via client-side.');
  }

  public async forgotPasswordSubmit(_username: string, _code: string, _password: string): Promise<void> {
    throw new Error('forgotPasswordSubmit is not supported for Auth0 via client-side.');
  }

  public async signIn(username?: string, _password?: string): Promise<void> {
    await this.auth0Client.loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}${ROUTES.SAGE_ID_SIGN_IN_TRIGGER}`,
        login_hint: username || ''
      }
    });
  }

  public async handleRedirectCallback(): Promise<void> {
    await this.auth0Client.handleRedirectCallback();
  }

  public async confirmSignIn(_user: Auth0User, _code: string, _mfaType: string | null): Promise<void> {
    throw new Error('confirmSignIn is not supported for Auth0.');
  }

  public async getPreferredMFA(_user: Auth0User): Promise<string> {
    throw new Error('getPreferredMFA is not supported for Auth0.');
  }

  public async setPreferredMFA(_user: Auth0User, _mfaMethod: string): Promise<void> {
    throw new Error('setPreferredMFA is not supported for Auth0.');
  }

  public async setupTOTP(_user: Auth0User): Promise<void> {
    throw new Error('setupTOTP is not supported for Auth0.');
  }

  public async verifyTotpToken(_user: Auth0User, _challengeAnswer: unknown): Promise<void> {
    throw new Error('verifyTotpToken is not supported for Auth0.');
  }

  public async verifyCurrentUserAttributeSubmit(_attribute: string, _code: string): Promise<void> {
    throw new Error('verifyCurrentUserAttributeSubmit is not supported for Auth0.');
  }

  public async verifyUserAttribute(_user: Auth0User, _attrs: string): Promise<void> {
    throw new Error('verifyUserAttribute is not supported for Auth0.');
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-unsafe-function-type, @typescript-eslint/no-wrapper-object-types
  public async signUp(_data: string | object, ..._restOfAttrs: string[]): Promise<void> {
    throw new Error('signUp is not supported for Auth0 via client-side.');
  }

  public async confirmSignUp(_username: string, _code: string, _options?: unknown): Promise<void> {
    throw new Error('confirmSignUp is not supported for Auth0.');
  }

  public async federatedSignIn(_options?: unknown): Promise<void> {
    throw new Error('federatedSignIn is not supported for Auth0.');
  }

  public async resendSignUp(_username: string): Promise<void> {
    throw new Error('resendSignUp is not supported for Auth0.');
  }
}
